import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Col, Collapse, Divider, Form, Row } from "antd";
import moment from "moment";
import { useOfferContext } from "../context/OfferContext";
import { ServiceProfileView } from "../../../components/OfferDetails/ServiceProfileView/ServiceProfileView";
import { ServiceProfileEdit } from "../../../components/OfferDetails/ServiceProfileEdit/ServiceProfileEdit";
import type { IOfferServiceProfileFormData } from "../context/types";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  fetchServiceProfileVersions,
  updateOfferServiceProfileVersion,
  offeringActions,
  updateOfferVersion,
  fetchOfferVersionsByIdWithParams,
} from "../../../store/slices/offering/offering";
import { checkIsLoading, toHttpFormatStartOfDay } from "../../../lib/utils";
import { IServiceProfile } from "../../../lib/types";
import { getServiceProfileColumns } from "./utils/getServiceProfileColumns";
import { TableColumn } from "../../../lib/interfaces";
import { IServiceProfileTableDataRecord } from "./utils/types";
import { ReactComponent as VersionsHiddenIcon } from "../../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../../img/versions_icons/versionsShown_icon.svg";
import { ReactComponent as DotsIcon } from "../../../img/3dots.svg";
import ToggleColumns from "../../../components/ToggleableVersionsTable/ToggleColumns";
import HistoryAndPropertyModal from "../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { hasAccess } from "../../../components/RolesModule/hasAccess";
import { IChangingRecordKeyNumber } from "../../../lib/types/IViewMode";
import "./ServiceProfileContainer.less";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ServiceProfileContainer = () => {
  const { serviceProfileForm, clearServiceProfile, activeOfferVersion } = useOfferContext();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const onDateFromUrl = searchParams.get("onDate")?.replace(" ", "+");
  const dispatch = useAppDispatch();
  const {
    serviceProfile,
    serviceProfileLoading,
    serviceProfileVersions,
    serviceProfileVersionsLoading,
  } = useAppSelector((state) => state.offering);

  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  // Колонки таблицы просмотра. Нужен общий тогглер, поэтому вынесены наверх
  const [columns, setColumns] = useState<TableColumn<IServiceProfileTableDataRecord>[]>(
    getServiceProfileColumns()
  );

  const showHistoryModal = () => {
    setHistoryModalVisible(true);
    setObjectId(serviceProfile?.id || null);
  };

  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(true);

  const toggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };

  // Флаг редактируемой версии [индекс в массиве версий, mode]
  const [childChangingMode, setChildChangingMode] = useState<IChangingRecordKeyNumber>();

  // Сброс режима редактирования при изменении активной версии оффера
  useEffect(() => {
    setChildChangingMode(undefined);
  }, [activeOfferVersion]);

  const changeViewMode = (params?: IChangingRecordKeyNumber) => {
    setChildChangingMode(params);
  };

  const onClickVersionIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (isVersionsOpen) {
      dispatch(offeringActions.cleanServiceProfileVersions());
      setChildChangingMode(undefined);
      setIsVersionsOpen(false);
    } else {
      serviceProfile?.id && dispatch(fetchServiceProfileVersions(serviceProfile.id));
      setIsVersionsOpen(true);
    }
  };

  const initialServiceProfileValues = useMemo((): Partial<IOfferServiceProfileFormData> => {
    const viewMode = childChangingMode?.[1];
    const version = childChangingMode && serviceProfileVersions[childChangingMode[0]];
    const fromDate = version && moment(version.fromDate);
    const toDate = version && moment(version.toDate);
    const baseValues: Partial<IOfferServiceProfileFormData> = {
      // название отображается только для типа профиля "Общий"
      id: version?.profileTypeId === 1 ? version?.id : undefined,
      profileTypeId: version?.profileTypeId,
      paymentTypeId: version?.paymentTypeId,
      invoicePeriodId: version?.invoicePeriodId,
      prepaymentPeriodId: version?.prepaymentPeriodId,
      prepaymentToPayDays: version?.prepaymentToPayDays,
      billToPayDays: version?.billToPayDays,
      billPeriodId: version?.billPeriodId,
      toDate,
    };
    switch (viewMode) {
      case "edit":
        return { ...baseValues, fromDate };
      case "add":
        return { ...baseValues, fromDate: undefined };
    }
    return {};
  }, [serviceProfileVersions, childChangingMode]);

  // Установка исходных значений профиля оплаты
  useEffect(() => {
    serviceProfileForm.setFieldsValue(initialServiceProfileValues);
  }, [initialServiceProfileValues]);

  const sumbitUpdateServiceProfileVersion = (values: IOfferServiceProfileFormData) => {
    const force = childChangingMode?.[1] === "edit";
    const version = serviceProfileVersions[childChangingMode![0]];
    const fromDateForCommon = force
      ? activeOfferVersion?.fromDate
      : toHttpFormatStartOfDay(values.fromDate);
    const editedServiceProfile: Partial<IServiceProfile> = {
      ...version,
      paymentTypeId: values.paymentTypeId,
      invoicePeriodId: values.invoicePeriodId,
      prepaymentPeriodId: values.prepaymentPeriodId,
      prepaymentToPayDays: values.prepaymentToPayDays,
      billToPayDays: values.billToPayDays,
      billPeriodId: values.billPeriodId,
    };
    if (activeOfferVersion?.offeringTypeId === 1) {
      const reloadOfferFromDate = force
        ? toHttpFormatStartOfDay(onDateFromUrl)
        : activeOfferVersion.fromDate!;
      dispatch(
        updateOfferVersion({
          offer: {
            ...activeOfferVersion,
            serviceProfileId: values.id,
            serviceProfile: { ...editedServiceProfile, id: values.id, fromDate: fromDateForCommon },
            fromDate: fromDateForCommon,
          },
          force,
          reloadOfferFromDate,
        })
      ).then(() => {
        setChildChangingMode(undefined);
        dispatch(
          fetchOfferVersionsByIdWithParams({
            id: activeOfferVersion.id!,
            searchParams: {
              allObjectsVersions: true,
              withChars: true,
            },
          })
        );
        dispatch(fetchServiceProfileVersions(values.id));
      });
    } else {
      dispatch(
        updateOfferServiceProfileVersion({
          serviceProfile: {
            ...editedServiceProfile,
            fromDate: toHttpFormatStartOfDay(values.fromDate),
          },
          force,
        })
      ).then(() => {
        setChildChangingMode(undefined);
      });
    }
  };

  // Добавление при создании версии
  const enrichedServiceProfileVersions =
    useMemo(() => {
      if (!childChangingMode) return serviceProfileVersions;
      if (childChangingMode[1] === "add") {
        const [pos] = childChangingMode;
        const baseVersion = serviceProfileVersions[pos];
        const firstPart = serviceProfileVersions.slice(0, pos);
        const secondPart = serviceProfileVersions.slice(pos);
        return [...firstPart, baseVersion, ...secondPart];
      }
      return serviceProfileVersions;
    }, [serviceProfileVersions, childChangingMode]) || [];

  return (
    <div className="service-profile-container">
      <Divider className="service-profile-container__upperDivider" />
      <Collapse activeKey={isCollapseOpen ? ["1"] : []} bordered={false} onChange={toggleCollapse}>
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="service-profile-container__pageTitle">
              <h3 id="offering-profile-title">Профиль оплаты</h3>
              {isCollapseOpen && (
                <Row align="middle" gutter={7}>
                  <Col>
                    {isVersionsOpen && <VersionsShownIcon onClick={onClickVersionIcon} />}
                    {!isVersionsOpen && <VersionsHiddenIcon onClick={onClickVersionIcon} />}
                  </Col>
                  <Col>
                    <DotsIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        showHistoryModal();
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Row>
          }
        >
          <Divider style={{ margin: 0 }} />
          <Row justify="end" style={{ marginTop: "10px" }}>
            <ToggleColumns
              tableName="offer-service-profile-table"
              columns={columns}
              setColumns={setColumns}
            />
          </Row>
          <ServiceProfileView
            serviceProfile={serviceProfile}
            isLoading={checkIsLoading(serviceProfileLoading, serviceProfileVersionsLoading)}
            columns={columns}
          />
          {isVersionsOpen &&
            enrichedServiceProfileVersions.map((version, idx) => (
              <div key={idx}>
                {childChangingMode?.[0] !== idx && (
                  <ServiceProfileView
                    columns={columns}
                    isChild
                    changeViewMode={changeViewMode}
                    idx={idx}
                    serviceProfile={version}
                    isEditingDisabled={Boolean(childChangingMode)}
                    offeringStatusId={activeOfferVersion?.offeringStatusId}
                    isLoading={checkIsLoading(serviceProfileVersionsLoading)}
                    offer={activeOfferVersion}
                  />
                )}
                {childChangingMode?.[0] === idx && (
                  <Form
                    form={serviceProfileForm}
                    labelAlign="left"
                    onFinish={sumbitUpdateServiceProfileVersion}
                  >
                    <ServiceProfileEdit
                      viewMode={childChangingMode[1]}
                      onSubmit={serviceProfileForm.submit}
                      onCancel={() => setChildChangingMode(undefined)}
                      clearServiceProfile={clearServiceProfile}
                      serviceProfile={version}
                      offer={activeOfferVersion}
                    />
                  </Form>
                )}
              </div>
            ))}
        </Collapse.Panel>
      </Collapse>
      <HistoryAndPropertyModal
        objectTypeSysname="service_billing_profile"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_WRITE)}
      />
    </div>
  );
};
